import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "pdf-page-navigator" }
const _hoisted_2 = { class: "navigator-header" }
const _hoisted_3 = { class: "navigator-title" }
const _hoisted_4 = { class: "page-count" }
const _hoisted_5 = { class: "page-thumbnails" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "thumbnail-image" }
const _hoisted_8 = {
  key: 0,
  class: "thumbnail-loading"
}
const _hoisted_9 = { class: "thumbnail-info" }
const _hoisted_10 = { class: "page-number" }
const _hoisted_11 = {
  key: 0,
  class: "annotation-badge"
}
const _hoisted_12 = { class: "navigator-controls" }
const _hoisted_13 = ["disabled", "title"]
const _hoisted_14 = { class: "current-page-indicator" }
const _hoisted_15 = ["disabled", "title"]

import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  totalPages: number;
  currentPage: number;
  annotationCounts: Record<number, number>;
  documentUrl: string; // PDF URL to load
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PDFPageNavigator',
  props: {
    totalPages: {},
    currentPage: {},
    annotationCounts: {},
    documentUrl: {}
  },
  emits: ["page-change"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const props = __props;
const emit = __emit;

const { t } = useI18n();

const thumbnailRefs = ref<Record<number, HTMLCanvasElement>>({});
const loadingThumbnails = ref(false);
let pdfDocumentInstance: any = null; // Don't use ref for PDF.js objects

const setThumbnailRef = (page: number, el: any) => {
  if (el) {
    thumbnailRefs.value[page] = el as HTMLCanvasElement;
  }
};

const getAnnotationCount = (page: number): number => {
  return props.annotationCounts[page] || 0;
};

const loadPdfDocument = async () => {
  try {
    const { getDocument, GlobalWorkerOptions } = await import('pdfjs-dist');
    
    GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.mjs',
      import.meta.url
    ).toString();

    const loadingTask = getDocument({
      url: props.documentUrl,
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
    });
    
    pdfDocumentInstance = await loadingTask.promise;
  } catch (error) {
    console.error('Error loading PDF for thumbnails:', error);
  }
};

const generateThumbnails = async () => {
  if (!pdfDocumentInstance || loadingThumbnails.value) return;

  loadingThumbnails.value = true;
  
  try {
    for (let pageNum = 1; pageNum <= props.totalPages; pageNum++) {
      const canvas = thumbnailRefs.value[pageNum];
      if (!canvas) continue;

      const page = await pdfDocumentInstance.getPage(pageNum);
      const viewport = page.getViewport({ scale: 0.2 });
      
      const context = canvas.getContext('2d');
      if (!context) continue;

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
    }
  } catch (error) {
    console.error('Error generating thumbnails:', error);
  } finally {
    loadingThumbnails.value = false;
  }
};

watch(() => props.documentUrl, async (newUrl: string) => {
  if (newUrl && props.totalPages > 1) {
    await loadPdfDocument();
    setTimeout(() => generateThumbnails(), 100);
  }
}, { immediate: true });

onMounted(async () => {
  if (props.documentUrl && props.totalPages > 1) {
    await loadPdfDocument();
    generateThumbnails();
  }
});

onUnmounted(() => {
  // Cleanup PDF document
  if (pdfDocumentInstance) {
    try {
      pdfDocumentInstance.destroy();
    } catch (e) {
      console.warn('Error destroying PDF document in navigator:', e);
    }
    pdfDocumentInstance = null;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h4", _hoisted_3, [
        _cache[2] || (_cache[2] = _createElementVNode("i", { class: "fas fa-file-pdf" }, null, -1)),
        _createTextVNode(" " + _toDisplayString(_ctx.$t('annotation.pages')), 1)
      ]),
      _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.totalPages) + " " + _toDisplayString(_ctx.$t('annotation.pages')), 1)
    ]),
    _createElementVNode("div", _hoisted_5, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.totalPages, (page) => {
        return (_openBlock(), _createElementBlock("div", {
          key: page,
          class: _normalizeClass(['page-thumbnail', { active: page === _ctx.currentPage }]),
          onClick: ($event: any) => (_ctx.$emit('page-change', page))
        }, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("canvas", {
              ref_for: true,
              ref: (el) => setThumbnailRef(page, el),
              class: "thumbnail-canvas"
            }, null, 512),
            (loadingThumbnails.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [...(_cache[3] || (_cache[3] = [
                  _createElementVNode("div", { class: "spinner-small" }, null, -1)
                ]))]))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("span", _hoisted_10, _toDisplayString(page), 1),
            (getAnnotationCount(page) > 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                  _cache[4] || (_cache[4] = _createElementVNode("i", { class: "fas fa-comment" }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(getAnnotationCount(page)), 1)
                ]))
              : _createCommentVNode("", true)
          ])
        ], 10, _hoisted_6))
      }), 128))
    ]),
    _createElementVNode("div", _hoisted_12, [
      _createElementVNode("button", {
        class: "nav-btn",
        disabled: _ctx.currentPage === 1,
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('page-change', _ctx.currentPage - 1))),
        title: _ctx.$t('annotation.previousPage')
      }, [...(_cache[5] || (_cache[5] = [
        _createElementVNode("i", { class: "fas fa-chevron-up" }, null, -1)
      ]))], 8, _hoisted_13),
      _createElementVNode("span", _hoisted_14, _toDisplayString(_ctx.currentPage) + " / " + _toDisplayString(_ctx.totalPages), 1),
      _createElementVNode("button", {
        class: "nav-btn",
        disabled: _ctx.currentPage === _ctx.totalPages,
        onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('page-change', _ctx.currentPage + 1))),
        title: _ctx.$t('annotation.nextPage')
      }, [...(_cache[6] || (_cache[6] = [
        _createElementVNode("i", { class: "fas fa-chevron-down" }, null, -1)
      ]))], 8, _hoisted_15)
    ])
  ]))
}
}

})